/***
 *
 *   AUTH LAYOUT
 *   Layout for the signup/signin pages.
 *
 *   DOCS
 *   https://docs.useMapMetrics Atlas.app/MapMetrics Atlas-web/components/layout
 *
 *   PROPS
 *   children: child view (component(s), required)
 *
 **********/

import { Animate, Logo, useLocation, useTranslation } from "components/lib";
import Axios from "axios";

export function AuthLayout({ children }) {
  const { t } = useTranslation();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user"));
  if (user) {
    if (user.token) {
      Axios.defaults.headers.common["Authorization"] = "Bearer " + user.token;
      Axios.defaults.headers.common["Accept-Language"] = user.locale;
    }

    // set the color mode
    user.dark_mode
      ? document.getElementById("app").classList.add("dark")
      : document.getElementById("app").classList.remove("dark");
  }
  // console.log(user.dark_mode, "darkmood");
  return (
    <main
      className="flex min-h-screen auth-gradient relative overflow-hidden"
      style={{
        backgroundImage: "url(/assets/Dashboard_11zon.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundBlendMode: "overlay",
      }}
    >
      {/* Clean background */}
      <div className="absolute inset-0 auth-gradient-overlay"></div>

      {location.pathname === "/forgotpassword" ? (
        <section className="flex justify-center items-center w-full p-12 relative z-10 [&>p]:-mt-2 [&_p]:mb-5 [&_p]:leading-5 [&_h1]:text-3xl [&_h1]:font-semibold [&_h1]:mb-6 [&_h1]:text-primary [&_h1]:text-center dark:[&_h1]:text-slate-50">
          <div className="max-w-full w-[658px] p-14 glass-card dark:glass-card-dark shadow-2xl fade-in">
            <div className="text-center">
              <img
                src="/assets/icons/recoverIcon.svg"
                color
                className="mx-auto mb-8"
                alt="recover icon"
              />
            </div>
            <Animate type="pop">{children}</Animate>
          </div>
        </section>
      ) : location.pathname === "/signup/confirm" ? (
        <section className="flex justify-center items-center w-full p-12 relative z-10 [&>p]:-mt-2 [&_p]:mb-5 [&_p]:leading-5 [&_h1]:text-3xl [&_h1]:font-semibold [&_h1]:mb-6 [&_h1]:text-primary [&_h1]:text-center dark:[&_h1]:text-slate-50">
          <div className="max-w-full w-[658px] p-14 glass-card dark:glass-card-dark shadow-2xl fade-in">
            <div className="text-center">
              <img
                src="/assets/icons/confirmIcon.svg"
                color
                className="mx-auto mb-20"
                alt="confrim icon"
              />
            </div>
            <Animate type="pop">{children}</Animate>
          </div>
        </section>
      ) : (
        <>
          {/* Left Panel - Login Form */}
          <section className="flex flex-col w-full lg:w-2/5 xl:w-1/3 h-screen lg:h-auto lg:max-h-screen p-4 sm:p-6 lg:p-8 relative z-10 lg:glass-card lg:dark:glass-card-dark lg:m-4 lg:rounded-3xl shadow-2xl slide-up [&_h1]:text-white [&_h1]:text-center [&_h1]:font-bold overflow-y-auto">
            <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full [&_form]:mb-4 [&_footer]:flex [&_footer]:justify-center [&_footer]:gap-x-2 [&_footer]:mt-6 [&_footer]:border-t [&_footer]:pt-4 [&_footer]:border-t-white/20 [&_footer]:text-white/80">
              <Animate type="pop">{children}</Animate>
            </div>
          </section>

          {/* Right Panel - Welcome Message */}
          <section className="hidden lg:flex flex-1 p-8 xl:p-12 justify-center items-center relative z-10 fade-in overflow-y-auto" style={{animationDelay: '0.3s'}}>
            <div className="text-center max-w-2xl">
              <div className="glass-card dark:glass-card-dark p-8 xl:p-12 shadow-2xl scale-in" style={{animationDelay: '0.6s'}}>
                <div className="mb-6">
                  <div className="w-16 h-16 xl:w-20 xl:h-20 mx-auto mb-4 bg-gray-700 rounded-full flex items-center justify-center shadow-lg shrink-0">
                    <svg className="w-8 h-8 xl:w-10 xl:h-10 shrink-0 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19l-7-7 7-7m8 14l7-7-7-7" />
                    </svg>
                  </div>
                </div>
                
                <h1 className="text-white text-3xl xl:text-4xl 2xl:text-5xl font-extralight mb-6 leading-tight tracking-wide">
                  MapAtlas Platform
                </h1>
                
                <p className="text-white/70 text-lg xl:text-xl font-light mb-8 leading-relaxed">
                  Build custom maps with complete data privacy
                </p>

                <div className="space-y-5 text-white/60 text-base xl:text-lg font-light">
                  <div className="flex items-center justify-center space-x-4">
                    <div className="w-1.5 h-1.5 bg-white/40 rounded-full"></div>
                    <span className="tracking-wide">Custom map creation tools</span>
                  </div>
                  <div className="flex items-center justify-center space-x-4">
                    <div className="w-1.5 h-1.5 bg-white/40 rounded-full"></div>
                    <span className="tracking-wide">Private data hosting</span>
                  </div>
                  <div className="flex items-center justify-center space-x-4">
                    <div className="w-1.5 h-1.5 bg-white/40 rounded-full"></div>
                    <span className="tracking-wide">Client-focused solutions</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
      
      {/* Logo in bottom right of entire page */}
      <div className="fixed bottom-4 right-4 z-50">
        <Logo color className="scale-50 opacity-80" />
      </div>
    </main>
  );
}
